import { useEffect, useRef } from 'react';
import { palette, admin } from '../../../styles/tokens';
import { ThreadHeader } from './ThreadHeader';
import { MessageBubble } from './MessageBubble';
import { Composer } from './Composer';
import type { Conversation, Message } from '../types';

interface Props {
  conversation: Conversation;
  messages: Message[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSend: (content: string) => void;
  sending: boolean;
  showTranslations: boolean;
  onToggleTranslations: () => void;
  onShelve: () => void;
  onUnshelve: () => void;
}

export function ThreadPanel({
  conversation, messages, loading, hasMore, onLoadMore,
  onSend, sending, showTranslations, onToggleTranslations,
  onShelve, onUnshelve,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);

  useEffect(() => {
    if (atBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversation.id]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    atBottomRef.current = scrollHeight - scrollTop - clientHeight < 40;
    if (scrollTop < 80 && hasMore && !loading) onLoadMore();
  };

  // Find last sent message index for read receipt
  let lastSentIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].isSent) { lastSentIdx = i; break; }
  }

  // Build elements with date separators and consecutive grouping
  let lastDate = '';
  let lastSenderId = '';
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const date = new Date(msg.created_at).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' });

    if (date !== lastDate) {
      lastDate = date;
      lastSenderId = '';
      elements.push(
        <div key={`date-${date}`} style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 8px' }}>
          <span style={{
            background: '#F0F0F0', borderRadius: 12, padding: '4px 16px',
            fontSize: 12, fontWeight: 500, color: '#8E8E93',
          }}>
            {date}
          </span>
        </div>,
      );
    }

    const isConsecutive = msg.sender_id === lastSenderId;
    lastSenderId = msg.sender_id;

    elements.push(
      <MessageBubble
        key={msg.id}
        message={msg}
        showTranslations={showTranslations}
        isLastSent={i === lastSentIdx}
        isConsecutive={isConsecutive}
      />,
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: palette.white, height: '100%', overflow: 'hidden' }}>
      <ThreadHeader
        conversation={conversation}
        showTranslations={showTranslations}
        onToggleTranslations={onToggleTranslations}
        onShelve={onShelve}
        onUnshelve={onUnshelve}
      />

      <div ref={scrollRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', background: palette.bg }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '8px 0' }}>
          {loading && messages.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: '#8E8E93' }}>Loading messages...</div>
          )}
          {!loading && messages.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: '#8E8E93' }}>No messages yet. Say hello!</div>
          )}
          {elements}
          <div style={{ height: 8 }} />
        </div>
      </div>

      {conversation.shelved ? (
        <div style={{
          padding: '12px 16px', borderTop: `1px solid ${admin.border}`,
          background: '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 13, color: '#92400E' }}>This conversation is shelved.</span>
          <button onClick={onUnshelve} style={{
            padding: '6px 14px', borderRadius: 6, border: 'none',
            background: palette.accent, color: palette.white,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>Reopen</button>
        </div>
      ) : (
        <Composer onSend={onSend} sending={sending} />
      )}
    </div>
  );
}
